/**
 * English UI catalogue for namespace 'rules' (W2-12b checker-rules): rule titles (Settings > Checker), the WHAT and
 * WHY text of every PH / GP problem and the labels of their fixes (DESIGN.md §5.10, §7.6). Rule files cannot import
 * this module (they run in the analysis worker and import only src/core/**), so they refer to the keys as strings
 * ('rules.<id>'); rules.test.ts checks that every key they use exists here and formats with the parameters given.
 * zh-CN lives in src/i18n/zh-CN/rules.ts (W3-09).
 */

import { catalogOf, defineMessages } from '../../../i18n/define';

// Shared ICU-lite fragments.
const PROP =
  '{prop, select, alpha {Opacity} moveX {Position X} moveY {Position Y} rotate {Rotation} speed {Scroll speed} scaleX {Size X} scaleY {Size Y} color {Colour} text {Text} incline {Note tilt} paint {Brush} gif {GIF progress} other {A property}}';
const PROP_LC =
  '{prop, select, alpha {opacity} moveX {position X} moveY {position Y} rotate {rotation} speed {scroll speed} scaleX {size X} scaleY {size Y} color {colour} text {text} incline {note tilt} paint {brush} gif {GIF progress} other {a property}}';
const NOTE_KIND = '{kind, select, tap {tap} drag {drag} flick {flick} hold {hold} other {note}}';
/** Plural of NOTE_KIND ('mixed': notes of several kinds). */
const NOTE_KINDS = '{kind, select, tap {taps} drag {drags} flick {flicks} hold {holds} other {notes}}';
const LINE_KIND =
  '{kind, select, normal {plain line} texture {image line} gif {GIF line} text {text line} paint {paint line} other {line}}';
const MEDIA_USE = '{use, select, image {Line image} audio {Hitsound} video {Video} shader {Shader} other {File}}';
const UI_ELEMENT =
  '{element, select, pause {pause button} combonumber {combo number} combo {combo label} score {score} bar {progress bar} name {song name} level {difficulty label} other {HUD element}}';
const VAR_SHAPE = 'float {a number} vec2 {a pair [x, y]} color {a colour [r, g, b, a]} other {another type}';
/**
 * An easing (parameters from `easeParams` in core/check/geometry.ts): "Stay, then jump", the custom curve with its
 * points, or the RPE easing name ({ease} stays untranslated) with the used part of the curve when sliced.
 */
const EASE =
  '{easeKind, select, hold {Stay, then jump} bezier {the custom curve {easePoints}} other {{ease}{easeSlice, select, part { (part {easeLeft} to {easeRight})} other {}}}}';
/** PH-22: which ignored RPE settings a line has (b = bpmfactor, a = anchor, s = skewControl; RPE names kept). */
const RPE_FIELDS =
  '{fields, select, b {bpmfactor {bpmfactor}} a {anchor {anchor}} s {skewControl} ba {bpmfactor {bpmfactor} and anchor {anchor}} bs {bpmfactor {bpmfactor} and skewControl} as {anchor {anchor} and skewControl} other {bpmfactor {bpmfactor}, anchor {anchor} and skewControl}}';

export const m = defineMessages('rules', {
  // ---- fix labels ----
  fixAddMusic: 'Add a music file…',
  fixAddPercentP: 'Change it to "{text}"',
  fixAddThirdPoint: 'Add a third point',
  fixAddWhiteColour: 'Add a white colour keyframe',
  fixChangeLineType: 'Change the line type…',
  fixChooseFile: 'Choose a file…',
  fixChooseGif: 'Choose a GIF…',
  fixChooseMusic: 'Choose another music file…',
  fixClampHandles: 'Clamp the handles into 0 to 1',
  fixClampOpacity: 'Clamp to 255',
  fixClampPreview: 'Clamp into the song ({start} to {end})',
  fixClampPreviewOpen: 'Clamp into the song (start at {start})',
  fixConvertToPng: 'Convert to PNG',
  fixConvertToTap: 'Convert to a tap',
  fixConvertToWav: 'Convert to WAV',
  fixDeleteDuplicate: 'Delete the duplicate',
  fixDeleteEffect: 'Delete the effect',
  fixDownscale: 'Downscale to {px} px',
  fixEditPreview: 'Edit the preview range…',
  fixExtendHold: 'Make it one snap long',
  fixFlatten: 'Flatten layers…',
  fixGoTo: 'Go to',
  fixGoToLine: 'Go to {line}',
  fixGoToSpan: 'Go to span',
  fixHoldFirstValue: 'Keep the first value',
  fixHoldVarFromStart: 'Keep the first value from {beat}',
  fixKeepTilt: 'Keep {deg}° for the whole chart',
  fixMakeIdentical: 'Use the curve {points}',
  fixMakeLinear: 'Make them linear',
  fixMoveNotesToNewLine: 'Move {count, plural, one {the note} other {the # notes}} to a new line',
  fixMoveTapLater: 'Move the tap one snap later',
  fixNudgeCurve: 'Nudge to {points}',
  fixOpenSpeedSettings: 'Open speed settings',
  fixPlainTail: 'Show "{text}" after the last keyframe',
  fixReencodeGif: 'Re-encode with {ms} ms frames',
  fixRemoveGifProgress: 'Remove GIF progress',
  fixRemoveIgnoredData: 'Remove the ignored data',
  fixRemoveReferences: 'Remove {count, plural, one {the reference} other {all # references}}',
  fixRenameHitsound: 'Rename to {path}{count, plural, =0 {} one { and re-point # note} other { and re-point # notes}}',
  fixRenameTo: 'Rename to {path}',
  fixReplaceShader: 'Replace the shader file…',
  fixResetRpeFields: 'Reset (Phira unchanged)',
  fixResetSlice: 'Use the whole curve',
  fixSelectBoth: 'Select both',
  fixSelectNotes: '{count, plural, one {Select the note} other {Select the # notes}}',
  fixSetOpacityFull: 'Set opacity to 100 %',
  fixSetVarDefault: 'Set {name} to {value}',
  fixSnapTogether: 'Snap to {beat}',
  fixSnapX: 'Snap X to the grid',
  fixUseEase: `Use ${EASE}`,
  fixWriteInteger: 'Write {version}',

  // ---- PH-01 ----
  ph01Title: 'Missing or unreadable file',
  ph01Missing: `${MEDIA_USE} "{path}" is missing from the project.`,
  ph01Undecodable: `${MEDIA_USE} "{path}" is in a format Phira cannot read ({codec}).`,
  ph01Why:
    'Phira refuses to load a chart whose files are missing or unreadable. This file is used {count, plural, one {once} other {# times}}.',
  // ---- PH-02 ----
  ph02Title: 'Music missing or unreadable',
  ph02None: 'The chart has no music file.',
  ph02Missing: 'The music file "{path}" is missing from the project.',
  ph02Undecodable: 'The music file "{path}" is in a format Phira cannot read ({codec}).',
  ph02Why: 'Phira cannot play a chart without its music and fails to load it.',
  // ---- PH-03 ----
  ph03Title: 'Overshooting easing on a type-on or erase text',
  ph03What: `The {case, select, reveal {type-on} other {erase}} text segment on {line} at {beat} ("{from}" to "{to}") uses ${EASE}, which overshoots.`,
  ph03Why:
    'Phira asks for {extra, plural, one {# character} other {# characters}} more than the text has and crashes while playing the chart.',
  // ---- PH-04 ----
  ph04Title: 'GIF with zero frame delay',
  ph04What:
    'The GIF "{path}" has a total frame delay of 0 ms ({count, plural, one {used by # line} other {used by # lines}}).',
  ph04Why: 'Phira loops forever while loading such a GIF as an animated line, so the chart never starts.',
  // ---- PH-05 ----
  ph05Title: 'File name Phira treats specially',
  ph05Skin: 'The file "{path}" ends in "click.png".',
  ph05SkinWhy: 'Phira treats a package containing such a file as a note skin and does not import it as a chart.',
  ph05Export: 'The package contains "{path}".',
  ph05ExportWhy: 'Phira treats a package containing export.json as a batch export and does not import it as a chart.',
  // ---- PH-06 ----
  ph06Title: 'Bezier speed in 1.7 speed mode',
  ph06What:
    '{count, plural, one {A Bezier speed segment} other {# Bezier speed segments}} on {line} (first at {beat}) while the project uses 1.7 speed curves.',
  ph06Why: "Phira's 1.7 speed integrator cannot evaluate Bezier speed and crashes when the chart loads.",
  // ---- PH-07 ----
  ph07Title: 'Effect variable Phira rejects',
  ph07Mismatch: `Effect variable "{name}" is written as {given, select, null {empty (null), which Phira reads as a number,} ${VAR_SHAPE}} but the shader expects {want, select, ${VAR_SHAPE}}.`,
  ph07DefaultType:
    'The shader "{path}" gives "{name}" a default of type {glslType}; Phira accepts only float, vec2 and vec4.',
  ph07DefaultValue: 'The shader "{path}" has a default value for "{name}" that Phira cannot read.',
  ph07Why: 'Phira rejects the effect and fails to load the whole chart.',
  // ---- PH-08 ----
  ph08Title: 'Use part of the curve that produces NaN',
  ph08What: `The segment of {owner, select, line {${PROP_LC} on {line}} effect {effect variable "{name}"} other {video {which, select, alpha {opacity} other {dim}}}} at {beat} uses ${EASE} sliced to [{left}, {right}], where both ends of the curve have the same height.`,
  ph08Why: 'Phira divides by zero there: the value becomes NaN and is not drawn correctly during this segment.',
  // ---- PH-09 ----
  ph09Title: 'Motion starts after beat 0',
  ph09What: `${PROP} of {line}{layer, plural, =0 {} other { (layer #)}} starts at {beat}; before that Phira continues the first segment backwards (value at 0:0: {value}).`,
  ph09Why: 'The line may move, turn or fade unexpectedly at the start of the chart.',
  ph09WhyAlpha: 'The opacity is below 0 there, which hides the line and all its notes at the start of the chart.',
  ph09WhyNaN: 'The curve gives NaN there, so the line is not drawn correctly at the start of the chart.',
  // ---- PH-10 ----
  ph10Title: 'Line without opacity',
  ph10What: '{line} has no opacity keyframes, so Phira draws it fully transparent.',
  ph10WhyNotes: 'Its notes still show, but the line itself is invisible in Phira.',
  ph10WhyDecor:
    'It has motion but no notes and no child lines, so it looks like a decoration meant to be seen, yet Phira shows nothing of it.',
  ph10WhyContent:
    'Its {kind, select, text {text} texture {image} gif {GIF} paint {brush strokes} other {content}} will not be visible in Phira.',
  // ---- PH-11 ----
  ph11Title: 'UI mover without opacity',
  ph11What: `The UI mover {line} moves the ${UI_ELEMENT} but has no opacity keyframes.`,
  ph11Why: 'Phira gives the HUD element the opacity of its line: without opacity keyframes the element is invisible.',
  // ---- PH-12 ----
  ph12Title: 'Notes on a UI mover line',
  ph12What: '{count, plural, one {# note is} other {# notes are}} on the UI mover {line}.',
  ph12Why:
    'Phira does not draw the notes of a UI mover line but still judges them and plays their hitsounds, so players must hit notes they cannot see.',
  // ---- PH-13 ----
  ph13Title: 'Negative note speed',
  ph13What:
    '{count, plural, one {# tap, drag or flick} other {# taps, drags or flicks}} on {line} {count, plural, one {has} other {have}} a negative speed (first at {beat}).',
  ph13Why:
    "With Phira's default note culling these notes are not drawn at all. Use the line's scroll speed for notes that come from the other side.",
  ph13WhyCullOff:
    "The preview has Phira's note culling turned off, but most players keep it on, and then these notes are not drawn at all.",
  // ---- PH-14 ----
  ph14Title: 'Speed 0 with distance effects',
  ph14What:
    '{count, plural, one {# note} other {# notes}} on {line} {count, plural, one {has} other {have}} speed 0 while the line has distance effects (first at {beat}).',
  ph14Why:
    "Phira computes a note's distance by dividing by its speed: with speed 0 the distance is NaN, and the distance effects use their last point for these notes.",
  // ---- PH-15 ----
  ph15Title: '%P% counter shown literally or as 0',
  ph15Tail: 'The last text keyframe of {line} ({beat}) is "{text}".',
  ph15TailWhy: 'After the last keyframe Phira shows the raw text, so "%P%" appears on screen.',
  ph15NotNumber: 'The counter on {line} at {beat} goes from "{from}" to "{to}", but "{bad}" is not a plain number.',
  ph15NotNumberWhy:
    'Phira needs the whole text without %P% to be a number and shows 0 otherwise. Put the label on its own text line.',
  ph15OneSided: 'Only one end of the text segment on {line} at {beat} contains %P% ("{from}" to "{to}").',
  ph15OneSidedWhy: 'Phira counts only when both ends contain %P%; here it shows "{shown}" until the next keyframe.',
  // ---- PH-16 ----
  ph16Title: 'Overshooting easing on a typewriter text',
  ph16What: `The {case, select, extend {typewriter} other {backspace}} text segment on {line} at {beat} ("{from}" to "{to}") uses ${EASE}, which overshoots.`,
  ph16Why:
    'Phira asks for {extra, plural, one {# character} other {# characters}} more than the text has; this may crash Phira (not verified).',
  // ---- PH-17 ----
  ph17Title: 'Distance effect ignored by Phira',
  ph17What:
    'The {which, select, pos {position} alpha {opacity} size {size} other {distance}} effect of {line} has two points and starts at 1 with Linear easing.',
  ph17Why:
    'Phira ignores such a curve completely, but RPE applies it, so the chart looks different in the two. A third point makes Phira apply it too.',
  // ---- PH-18 ----
  ph18Title: 'Animated note tilt',
  ph18What: 'The note tilt of {line} changes over time (it is {from}° at 0:0 and changes at {beat}).',
  ph18Why: 'Phira reads the tilt only at the start of the chart and keeps it: the later changes have no effect.',
  // ---- PH-19 ----
  ph19Title: 'Eased speed played straight',
  ph19What:
    '{count, plural, one {# eased speed segment} other {# eased speed segments}} on {line} (first at {beat}) {count, plural, one {plays} other {play}} as a straight ramp in Phira.',
  ph19Why:
    'The project uses the classic speed mode, where Phira ignores speed easings; they are kept for RPE. Switch to 1.7 speed curves to make Phira play them.',
  // ---- PH-20 ----
  ph20Title: 'Bezier curves Phira merges',
  ph20What:
    "{count, plural, one {A Bezier segment} other {# Bezier segments}} with the curve {points} share Phira's rounded cache key with the curve {winner} on {line}.",
  ph20Why: 'Phira keeps one curve per key rounded to 0.01, so these segments play with the shape of the first curve.',
  // ---- PH-21 ----
  ph21Title: 'Easing that looks different in RPE',
  ph21What:
    '{count, plural, one {# segment} other {# segments}} on {line} (first at {beat}) {count, plural, one {uses} other {use}} InOutBack or InOutElastic.',
  ph21Why:
    "Phira's version of these two easings differs from RPE's, so the motion looks different when the chart is opened in RPE.",
  // ---- PH-22 ----
  ph22Title: 'RPE line settings Phira ignores',
  ph22What: `{line} has RPE settings that Phira ignores: ${RPE_FIELDS}.`,
  ph22Why:
    "RPE uses them (bpmfactor changes the line's tempo, anchor moves its image, the skew control slants its notes), so the chart plays differently in RPE and Phira.",
  // ---- PH-23 ----
  ph23Title: 'Notes hidden by negative opacity',
  ph23What:
    'The opacity of {line} is below 0 around {beat}: {count, plural, one {# note is} other {# notes are}} hidden when {count, plural, one {it is} other {they are}} hit.',
  ph23Why: 'Phira hides all notes of a line whose opacity is below 0, but still judges them and plays their hitsounds.',
  // ---- PH-24 ----
  ph24Title: 'Line data Phira ignores',
  ph24What: `{item, select, text {Text} paint {Brush} gif {GIF progress} other {Data}} on {line} is ignored by Phira, because it is a ${LINE_KIND}.`,
  ph24WhyTextImage: 'Phira shows text only on lines without an image.',
  ph24WhyTextPaint: 'A line with brush keyframes is a paint line, and Phira does not show its text.',
  ph24WhyPaintImage: 'Phira paints only on lines without an image.',
  ph24WhyGifPlain: 'GIF progress only applies to lines whose image is a GIF.',
  // ---- PH-25 ----
  ph25Title: 'More than 4 layers',
  ph25What: `${PROP} of {line} uses {count} layers.`,
  ph25Why: 'Phira plays every layer, but RPE shows only the first 4, so the others are hard to edit there.',
  // ---- PH-26 ----
  ph26Title: 'Several lines move the same HUD element',
  ph26What: `{count} lines move the ${UI_ELEMENT}.`,
  ph26Why: 'Phira uses only the last of them ({winner}); the others have no effect on the HUD.',
  // ---- PH-27 ----
  ph27Title: 'Very large line image',
  ph27What: 'The line image "{path}" is {w} × {h} px.',
  ph27Why: 'Phira draws line images without mipmaps, so very large images shimmer when shrunk on screen.',
  // ---- PH-28 ----
  ph28Title: 'Brush size depends on the screen',
  ph28What: '{line} is a paint line.',
  ph28Why:
    'Phira measures the brush in screen pixels, so strokes look thinner on large or high-resolution screens and thicker on small ones.',
  // ---- PH-29 ----
  ph29Title: 'Attached video tinted by the line colour',
  ph29What: 'The video "{path}" is attached to {line}, which has no colour keyframes.',
  ph29Why:
    "Phira tints an attached video with its line's colour; without colour keyframes it uses the judge line colour (gold or blue while the combo lasts).",
  // ---- PH-30 ----
  ph30Title: 'Effect variable starts after the effect',
  ph30What: 'The effect variable "{name}" starts at {beat}, after the effect starts at {start}.',
  ph30Why:
    'Before its first keyframe Phira continues the first segment backwards, so the effect may start with an unexpected value.',
  // ---- PH-31 ----
  ph31Title: 'Non-ASCII file name',
  ph31What: 'The file name "{path}" contains characters outside ASCII.',
  ph31Why: 'Zip tools and Phira may decode such names differently, so the file may not be found after export.',
  // ---- PH-32 ----
  ph32Title: 'Hold body not drawn',
  ph32What:
    '{count, plural, one {# hold} other {# holds}} on {line} (first at {beat}) {count, plural, one {has its} other {have their}} tail nearer to the line than the head.',
  ph32Why: 'The notes scroll backwards during these holds, so Phira draws only their head and tail, not the body.',
  // ---- PH-33 ----
  ph33Title: 'Custom shader does not compile',
  ph33What: 'The custom shader "{path}" does not compile: {error}',
  ph33Why: 'Phira compiles the same GLSL ES 1.00 code and will most likely fail to load the chart.',
  // ---- PH-34 ----
  ph34Title: 'RPEVersion is not an integer',
  ph34What:
    'RPEVersion is written as {kind, select, float {the decimal} other {the text}} {lexeme}, which Phira reads as {effective}.',
  ph34Why:
    'Phira reads a decimal RPEVersion as 160 and parses a text one, which can change how speed easings play; an integer avoids surprises.',
  // ---- PH-35 ----
  ph35Title: 'GIF progress on an image that is not a GIF',
  ph35What:
    '{line} has {auto, select, yes {"Play automatically" on} other {GIF progress keyframes}} but its image "{path}" is not a GIF.',
  ph35Why: 'Phira reads such an image with its GIF decoder and fails to load the whole chart.',
  // ---- PH-36 ----
  ph36Title: 'Song-select preview outside the music',
  ph36Negative: 'The song-select preview starts at {start}, before the music.',
  ph36Order: 'The song-select preview starts at {start} but ends earlier, at {end}.',
  ph36Beyond: 'The song-select preview ends at {end}, but the music is only {length} long.',
  ph36BeyondNull:
    'The song-select preview starts at {start}; Phira checks {end} (start + 1 s) against the music length {length}.',
  ph36Why:
    'Phira refuses to open a chart whose preview does not end at least 0.5 s before the music ends, and crashes when the preview starts after it ends.',
  ph36WhyShort:
    'Phira refuses to open the chart, and the music is too short for any preview range that ends 0.5 s before the music ends.',
  // ---- PH-37 ----
  ph37Title: 'Zero-length hold',
  ph37What: 'The hold on {line} at {beat} has no length.',
  ph37Why: 'Phira judges it like a tap and shows it only for an instant.',
  // ---- PH-38 ----
  ph38Title: 'Hitsound file with a reserved name',
  ph38What:
    'The package contains a file named "{path}"{count, plural, =0 {.} one { (# note uses that name).} other { (# notes use that name).}}',
  ph38Why: 'Phira plays its built-in sound for notes whose hitsound is "{path}", never this file.',
  // ---- PH-39 ----
  ph39Title: 'Bezier handle outside 0 to 1',
  ph39What: `The Bezier curve {points} of ${PROP_LC} on {line} at {beat} has a handle x outside 0 to 1.`,
  ph39Why: "Phira plays it, but RPE's curve editor may not show or keep it.",

  // ---- GP-01 ----
  gp01Title: 'Tap right after a nearby flick',
  gp01What: 'This tap is {ms} ms after a flick {px} px away.',
  gp01Why: 'Players who slide through the flick will hit this tap early, and it will count as Good.',
  // ---- GP-02 ----
  gp02Title: 'Tap right after a nearby drag',
  gp02What: 'This tap is {ms} ms after a drag {px} px away.',
  gp02Why: 'Players hit the drag and this tap with one touch, so the tap is judged too early.',
  gp02WhyLater: 'Players who keep their finger on the drag must lift it and touch again very quickly to hit this tap.',
  // ---- GP-03 ----
  gp03Title: 'Overlapping notes',
  gp03What:
    '{pair, select, taptap {Two taps} taphold {A tap and a hold} holdhold {Two holds} other {Two notes}} at {beat} are only {px} px apart.',
  gp03Why: 'One touch hits only one of them, so players need two fingers on the same spot.',
  // ---- GP-04 ----
  gp04Title: 'Note outside the song',
  gp04Before:
    '{count, plural, one {# note} other {# notes}} on {line} {count, plural, one {comes} other {come}} before the music starts (first at {beat}).',
  gp04BeforeWhy:
    'With an offset of {offset} ms these notes fall before the start of the music, where players cannot hit them in time with the song.',
  gp04After:
    '{count, plural, one {# note} other {# notes}} on {line} {count, plural, one {ends} other {end}} after the music ends (first at {beat}).',
  gp04AfterWhy:
    'The music is over by then, so these notes play in silence, and Phira may end the chart before players reach them.',
  // ---- GP-05 ----
  gp05Title: 'Note hit off screen',
  gp05What:
    '{count, plural, one {# note} other {# notes}} on {line} {count, plural, one {is} other {are}} hit at a point off screen (first at {beat}).',
  gp05WhyReachable:
    'Players can only hit them by touching in line with the note somewhere on screen, without seeing where.',
  gp05WhyUnreachable:
    '{count, plural, one {# of them} other {# of them}} cannot be reached from the screen at all, so players always miss {count, plural, one {it} other {them}}.',
  // ---- GP-06 ----
  gp06Title: 'Short read time',
  gp06What: `{count, plural, one {This ${NOTE_KIND} on {line} at {beat} is visible for only {ms} ms before it is hit.} other {# ${NOTE_KINDS} on {line} at {beat} are visible for only {ms} ms before they are hit.}}`,
  gp06Never: `{count, plural, one {This ${NOTE_KIND} on {line} at {beat} is not visible before it is hit.} other {# ${NOTE_KINDS} on {line} at {beat} are not visible before they are hit.}}`,
  gp06Why:
    'Players need about {min} ms to read a note. Measured with the active skin and Phira settings on a {w} × {h} screen.',
  // ---- GP-07 ----
  gp07Title: 'Hold touching another note',
  gp07Start: `A ${NOTE_KIND} at {beat} touches the start of a hold ({px} px away).`,
  gp07StartWhy: 'One touch starts the hold and may also hit this note, or players cannot tell the two apart.',
  gp07End: `A ${NOTE_KIND} at {beat} touches the end of a hold ({px} px away).`,
  gp07EndWhy: 'Players still holding must lift and touch the same spot again at that moment, which is easy to miss.',
  // ---- GP-08 ----
  gp08Title: 'Note far from the line centre',
  gp08What:
    '{count, plural, one {# note} other {# notes}} on {line} {count, plural, one {is} other {are}} more than {max} px from the line centre (first at {beat}, X {x}).',
  gp08Why: 'Notes this far out are at or past the screen edge on an unmoved line and are hard to see and reach.',
  gp08WhyFake: 'These decoration notes are at or past the screen edge on an unmoved line, so players may not see them.',
  // ---- GP-09 ----
  gp09Title: 'Almost simultaneous notes',
  gp09What: 'Two notes at {beat} are {ms} ms apart instead of exactly together.',
  gp09Why:
    'Phira highlights notes as simultaneous only when their times are exactly equal, so these show without the highlight.',
  // ---- GP-10 ----
  gp10Title: 'Opacity above 255',
  gp10What:
    '{count, plural, one {# opacity keyframe} other {# opacity keyframes}} on {line} {count, plural, one {is} other {are}} above 255 (first at {beat}: {value}).',
  gp10Why:
    'Opacity above 255 looks the same as 255, so fades seem to start late. Values below 0 that hide notes are fine.',
  gp10WhatSum:
    'The {layers} opacity layers of {line} add up to more than 255 {count, plural, one {at # keyframe} other {at # keyframes}} (first at {beat}: {value}).',
  gp10WhySum:
    'Phira draws the sum of the layers, and a sum above 255 looks the same as 255, so fades seem to start late. Sums below 0 that hide notes are fine.',
});

export default catalogOf(m);
